import React, { FC, useContext, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Button, Loader } from 'ncoded-component-library';
import { WithdrawButton } from '../WithdrawButton';
import Nbio from '../Nbio';
import LeadContext from '../../../providers/Lead/Lead.context';
import ChatIcon from 'icons/Chat.icon';
import CheckcircleIcon from 'icons/Checkcircle.icon';
import WarningExclamationIcon from 'icons/WarningExclamation.icon';
import SessionLink from '../SessionLink';
import PreviewNbioModal from 'components/PreviewNbioModal';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useCallbackRef from 'hooks/useCallbackRef';

import './BidderStepActions.styles.scss';

type BidderStepActionsProps = {
  className?: string;
};

const BidderStepActions: FC<BidderStepActionsProps> = ({ className }) => {
  const {
    leadId,
    nbio,
    isOfferRejected,
    isOfferAccepted,
    sessionId,
    enquiry,
    dealRoom,
  } = useContext(LeadContext);

  const [previewModal, previewModalRef] = useCallbackRef<ModalRef>();

  const classes = classNames('bidder-step-actions', className);

  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);

  const WithdrawAction = useMemo(
    () => (
      <WithdrawButton
        leadId={leadId}
        isLoading={isLoading}
        dealId={dealRoom?._id}
        setIsLoading={setIsLoading}
      />
    ),
    [dealRoom?._id, isLoading, leadId],
  );

  const Actions = useMemo(() => {
    if (isOfferRejected)
      return (
        <>
          <Nbio buttonContent={t('counterOffer')} />
          {WithdrawAction}
        </>
      );

    if (!nbio)
      return (
        <>
          <Nbio />
          {WithdrawAction}
        </>
      );

    if (!isOfferAccepted)
      return (
        <>
          <Button variant="outline" onClick={previewModal?.open}>
            {t('previewNbio')}
          </Button>
          <Nbio variant="link" buttonContent={t('replaceNbio')} />
          <PreviewNbioModal ref={previewModalRef} nbio={nbio} />
        </>
      );

    if (sessionId)
      return (
        <>
          <SessionLink listingEnquiryId={enquiry?._id} sessionId={sessionId} />
          {WithdrawAction}
        </>
      );

    return WithdrawAction;
  }, [
    WithdrawAction,
    enquiry?._id,
    isOfferAccepted,
    isOfferRejected,
    nbio,
    previewModal?.open,
    previewModalRef,
    sessionId,
    t,
  ]);

  const Content = useMemo(() => {
    if (isOfferRejected)
      return (
        <>
          <p className="title rejected-heading">
            <WarningExclamationIcon />
            {t('alert')}
          </p>

          <p>{t('buyerActionsOfferRejectedMessage')}</p>
        </>
      );

    if (!nbio)
      return (
        <>
          <p className="title">
            <ChatIcon />
            {t('usefulInformation')}
          </p>

          <p>{t('buyerActionsMissingOffer')}</p>
        </>
      );

    if (isOfferAccepted)
      return (
        <>
          <p className="title">
            <CheckcircleIcon />
            {t('congrats')}
          </p>

          <p>{t('buyerActionsOfferAcceptMessage')}</p>
        </>
      );

    return (
      <>
        <p className="title">
          <ChatIcon />
          {t('usefulInformation')}
        </p>

        <p>{t('buyerActionsOfferMessage')}</p>
      </>
    );
  }, [isOfferAccepted, isOfferRejected, nbio, t]);

  return (
    <div className={classes}>
      {isLoading && <Loader />}

      {Content}

      <div className="actions">{Actions}</div>
    </div>
  );
};

export default BidderStepActions;
